import type { Note, NoteLabel, TimeSignature } from '@/types';
import { HitStatus } from '@/types';
import { readMusicXmlFile, recolorOsmdByTheme, excludeGraceNotes } from '@/utils';
import { useToast } from '@/composables/useToast';
import { useMidiNoteMapping } from '@/composables/useMidiNoteMapping';
import type { DrumKey } from '@/config/drumMapping';
import type { Flow } from 'vexflow';
import { MXLHelper, NoteEnum, NoteHeadShape, OpenSheetMusicDisplay, SourceMeasure, VexFlowGraphicalNote, type Note as OSMDNote, VexFlowMusicSheetCalculator, type IOSMDOptions } from 'opensheetmusicdisplay';
import { computed, markRaw, ref, shallowRef, watch, inject, provide, type InjectionKey } from 'vue';
import { useSettings } from './useSettings';

const proto = VexFlowMusicSheetCalculator.prototype;
const originalFn = proto.calculateMeasureXLayout;

// OSMD doesn't parse notehead `parentheses` or preserve "back slashed" as a
// distinct shape, but it does parse `color` into sourceNote.noteheadColor
// (confirmed via OSMD's own demo wiki) — the only per-note tag that survives
// its XML→object parsing untouched. We smuggle the ghost/rimshot flag through
// as a sentinel color, then read it back and immediately repaint it in processNotes.
const GHOST_NOTEHEAD_SENTINEL = '#010101';
const RIMSHOT_NOTEHEAD_SENTINEL = '#020202';

// Grace notes borrow time from the beat before the main note they decorate.
// Each grace note in a run is played this many ms earlier than the one after it,
// so the grace note immediately preceding the main note starts GRACE_NOTE_OFFSET_MS
// early, the one before that starts 2x early, etc.
const GRACE_NOTE_OFFSET_MS = 40;

const tagDecoratedNoteheads = (xmlDoc: XMLDocument): void => {
  xmlDoc.querySelectorAll('notehead[parentheses="yes"]').forEach(notehead => {
    notehead.setAttribute('color', GHOST_NOTEHEAD_SENTINEL);
  });
  xmlDoc.querySelectorAll('notehead').forEach(notehead => {
    if (notehead.textContent?.trim() === 'back slashed') {
      notehead.setAttribute('color', RIMSHOT_NOTEHEAD_SENTINEL);
    }
  });
};

const NOTEHEAD_OVERLAY_CLASS = 'notehead-decoration-overlay';

const getNoteheadPathAndGroup = (graphicalNote: VexFlowGraphicalNote) => {
  const svgElement = graphicalNote.getSVGGElement();
  const vfnote = svgElement.querySelector('g.vf-note');
  const vfnoteIndex = graphicalNote.vfnoteIndex;
  const group = vfnote?.querySelectorAll('.vf-notehead')[vfnoteIndex] as SVGGElement | undefined;
  const path = group?.children[0] as SVGGraphicsElement | undefined;
  if (!group || !path) return undefined;
  return { path, group };
};

const clearNoteheadOverlay = (graphicalNote: VexFlowGraphicalNote): void => {
  graphicalNote.getSVGGElement().querySelectorAll(`.${NOTEHEAD_OVERLAY_CLASS}`).forEach(el => el.remove());
};

// Diagonal stroke across the notehead — rimshot.
export const drawRimshotSlash = (graphicalNote: VexFlowGraphicalNote, color: string): void => {
  try {
    const found = getNoteheadPathAndGroup(graphicalNote);
    if (!found) return;
    clearNoteheadOverlay(graphicalNote);

    const bbox = found.path.getBBox();
    const cx = bbox.x + bbox.width / 2;
    const cy = bbox.y + bbox.height / 2;
    const dx = bbox.width * 1.5;
    const dy = bbox.height * 1.2;

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(cx - dx / 2));
    line.setAttribute('y1', String(cy - dy / 2));
    line.setAttribute('x2', String(cx + dx / 2));
    line.setAttribute('y2', String(cy + dy / 2));
    line.setAttribute('stroke', color);
    line.setAttribute('stroke-width', '1.4');
    line.setAttribute('class', NOTEHEAD_OVERLAY_CLASS);
    found.group.appendChild(line);
  } catch (err) {
    console.error('Failed to draw rimshot slash:', err);
  }
};

// "(" and ")" flanking the notehead — ghost note.
export const drawGhostParentheses = (graphicalNote: VexFlowGraphicalNote, color: string): void => {
  try {
    const found = getNoteheadPathAndGroup(graphicalNote);
    if (!found) return;
    clearNoteheadOverlay(graphicalNote);

    const bbox = found.path.getBBox();
    const cy = bbox.y + bbox.height / 2;
    const pad = bbox.width * 0.45;
    const fontSize = bbox.height * 1.3;

    const makeParen = (x: number, char: '(' | ')') => {
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(x));
      text.setAttribute('y', String(cy));
      text.setAttribute('font-size', String(fontSize));
      text.setAttribute('fill', color);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'central');
      text.setAttribute('class', NOTEHEAD_OVERLAY_CLASS);
      text.textContent = char;
      return text;
    };

    found.group.appendChild(makeParen(bbox.x - pad, '('));
    found.group.appendChild(makeParen(bbox.x + bbox.width + pad, ')'));
  } catch (err) {
    console.error('Failed to draw ghost parentheses:', err);
  }
};

const NOTE_LABEL_OVERLAY_CLASS = 'note-label-overlay';

// OSMD's own fallback for a <direction>/<words> element it can't otherwise
// classify (GraphicalUnknownExpression) — see EngravingRules.DefaultFontFamily
// and EngravingRules.UnknownTextHeight (2.0 units * unitInPixels (10) = 20px).
// MusicXML <words> elements almost never carry their own font-size/font-family
// (those live in the score-wide <defaults><word-font>, which distributeNoteLabelDirections
// doesn't read), so this is what OSMD would actually have rendered, not a size
// derived from the notehead.
const DEFAULT_LABEL_FONT_FAMILY = 'Times New Roman';
const DEFAULT_LABEL_FONT_SIZE_PX = 20;

const clearNoteLabelOverlay = (graphicalNote: VexFlowGraphicalNote): void => {
  graphicalNote.getSVGGElement().querySelectorAll(`.${NOTE_LABEL_OVERLAY_CLASS}`).forEach(el => el.remove());
};

// Topmost y a note's own glyphs reach, in its local SVG coordinate space
// (smaller y = higher on the page). The notehead's own bbox alone isn't
// enough: VexFlow draws a note's stem and beam as separate sibling groups
// (id `${noteId}-stem` / `${noteId}-beam0`) rather than children of the
// notehead, so a label anchored to just the notehead overlaps a stemmed,
// beamed note's stem/beam instead of clearing it.
const getNoteTopY = (graphicalNote: VexFlowGraphicalNote, noteheadBbox: DOMRect): number => {
  const noteGroup = graphicalNote.getSVGGElement();
  const noteId = noteGroup.id;
  const measureGroup = noteGroup.parentElement;
  if (!noteId || !measureGroup) return noteheadBbox.y;

  const stem = measureGroup.querySelector<SVGGraphicsElement>(`#${CSS.escape(noteId)}-stem`);
  const stemBbox = stem?.getBBox();
  return stemBbox ? Math.min(noteheadBbox.y, stemBbox.y) : noteheadBbox.y;
};

// y of the top staff line, taken from the first line path of the note's
// enclosing measure group (OSMD draws the stave, then every voice's notes,
// as direct children of one `g.vf-measure` group per measure). Grace notes
// nest inside their main note's `g.vf-modifiers` instead of being a direct
// child of that group, and other modifiers (e.g. a dot) can draw a stray
// `<path>` straight into that same `g.vf-modifiers` — so climb to the
// measure by class rather than stopping at the first ancestor with any
// direct `<path>` child.
const getStaffTopY = (graphicalNote: VexFlowGraphicalNote): number | null => {
  const measureGroup = graphicalNote.getSVGGElement().closest<SVGGElement>('g.vf-measure');
  const line = measureGroup?.querySelector<SVGGraphicsElement>(':scope > path');
  return line ? line.getBBox().y - 48 : null;
};

// Arbitrary text label drawn above a note ourselves, instead of left as a
// <direction>/<words> element for OSMD to render — see distributeNoteLabelDirections
// for why (MuseScore's per-instance collision-avoidance offsets on those
// elements inflate a whole system's rendered height when OSMD honors them
// literally). Originally built for drum sticking ("R"/"L"), but the content is
// just whatever text the caller passes — nothing here is sticking-specific.
export const drawNoteLabel = (graphicalNote: VexFlowGraphicalNote, label: NoteLabel, color: string): void => {
  try {
    const found = getNoteheadPathAndGroup(graphicalNote);
    if (!found) return;
    clearNoteLabelOverlay(graphicalNote);

    const bbox = found.path.getBBox();
    const cx = bbox.x + bbox.width / 2;
    // The source MusicXML's own font-size/family when it specified one
    // per-instance, otherwise OSMD's own default — matches what OSMD would
    // have rendered had it kept the <words> element instead of us stripping it.
    const fontSize = `${DEFAULT_LABEL_FONT_SIZE_PX}px`;

    const fontFamily = label.fontFamily ?? DEFAULT_LABEL_FONT_FAMILY;
    // Anchored to the staff's top line so every label in a system sits on the
    // same horizontal line regardless of stem length; falls back to the note's
    // own topmost glyph (stem included) when the staff line isn't found.
    const topY = getStaffTopY(graphicalNote) ?? getNoteTopY(graphicalNote, bbox);
    
    const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textEl.setAttribute('x', String(cx));
    textEl.setAttribute('y', String(topY - bbox.height * 4));
    textEl.setAttribute('font-size', fontSize);
    textEl.setAttribute('font-family', fontFamily);
    textEl.setAttribute('fill', color);
    textEl.setAttribute('text-anchor', 'middle');
    textEl.setAttribute('dominant-baseline', 'central');
    textEl.setAttribute('class', NOTE_LABEL_OVERLAY_CLASS);
    textEl.textContent = label.text;
    found.group.appendChild(textEl);
  } catch (err) {
    console.error('Failed to draw note label:', err);
  }
};

// The calculator prototype is global, so this tick-spacing override applies to every
// OSMD instance — and several are live at once (the game view's, plus the sections
// editor's inside it). EngravingRules is the only per-instance object reachable from
// here, so applyRenderMode's `useDefaultMeasureXLayout` stamps the opt-out onto it.
proto.calculateMeasureXLayout = function (measures) {
  if ((this.rules as unknown).useDefaultMeasureXLayout) {
    return originalFn.call(this, measures);
  }
  for (const measure of measures) {
    if (!measure) return;
    // Keep positions exact; floor spacing only after subtracting the fractions.
    const allTickableIntervals: Array<{startTick: Flow.Fraction, endTick: Flow.Fraction}> = [];
    for (const vfVoice of Object.values(measure.vfVoices)) {
      let currentStartTick: Flow.Fraction | undefined;
      for (const tickable of vfVoice.tickables) {
        const startTick = currentStartTick ?? tickable.ticks.clone().set(0, 1);
        const endTick = startTick.clone()
          .add(tickable.ticks.numerator, tickable.ticks.denominator).simplify();
        tickable.tickNumerator = tickable.ticks.numerator;
        tickable.tickDenominator = tickable.ticks.denominator;
        tickable.tickDuration = Math.floor(tickable.tickNumerator / tickable.tickDenominator);
        tickable.startTickNumerator = startTick.numerator;
        tickable.startTickDenominator = startTick.denominator;
        tickable.startTick = Math.floor(startTick.value());
        allTickableIntervals.push({
          startTick,
          endTick
        });
        currentStartTick = endTick;
      }
    }

    // Build nextTickableStartTick map: for each start tick, get the next unique start tick
    const uniqueStartTicks = [...new Map(allTickableIntervals.map(i => [i.startTick.toString(), i.startTick])).values()]
      .sort((a, b) => a.equals(b) ? 0 : a.lessThan(b) ? -1 : 1);
    const nextTickableStartTick: Record<string, Flow.Fraction> = {};
    for (let i = 0; i < uniqueStartTicks.length; i++) {
      const currentStartTick = uniqueStartTicks[i];
      if (i + 1 < uniqueStartTicks.length) {
        nextTickableStartTick[currentStartTick.toString()] = uniqueStartTicks[i + 1];
      } else {
        // For the last start tick, use the maximum end tick from all intervals with that start tick
        const maxEndTick = allTickableIntervals
          .filter(i => i.startTick.equals(currentStartTick))
          .reduce((max, i) => i.endTick.greaterThan(max) ? i.endTick : max, currentStartTick);
        nextTickableStartTick[currentStartTick.toString()] = maxEndTick;
      }
    }

    // Process tickables with accumulated data
    for (const vfVoice of Object.values(measure.vfVoices)) {
      // console.log("measure", measure.measureNumber)
      // console.log({nextTickableStartTick})
      for (const tickable of vfVoice.tickables) {
        const startTickKey = `${tickable.startTickNumerator}/${tickable.startTickDenominator}`;
        const tickSpacing = Math.floor(nextTickableStartTick[startTickKey].clone()
          .subtract(tickable.startTickNumerator, tickable.startTickDenominator).simplify().value());
        if (tickSpacing <= 512) {
          // 32nd note
          tickable.paddingRight = -8;
        }
        else if (tickSpacing === 682) {
          // 16th sextuplet
          tickable.paddingRight = -5;
        }
        else if (tickSpacing === 1024) {
          // 16th note
          tickable.paddingRight = -2;
        }
        else if (tickSpacing === 1365) {
          // 8th note triplet
          tickable.paddingRight = 0;
        }
        else if (tickable.tickDuration === 2048 &&
          tickable.stem?.stem_direction === 1 &&
         tickSpacing === 2048 &&
         !tickable.beam
        ) {
          // 8th note without beam
          tickable.paddingRight = 2;
        }
        else if (tickSpacing === 4096) {
          tickable.paddingRight = +19;
        }
        else if (tickSpacing === 8192) {
          tickable.paddingRight = +72;
        }
        else if (tickSpacing === 16384) {
          tickable.paddingRight = +250;
        }
        else {
          // console.log(tickSpacing);
        }

        // Grace notes attached to this tickable are tickables in their own right
        // (formatted by the GraceNoteGroup's internal voice/formatter), so they
        // need their own paddingRight to pull them closer to the main note and
        // to each other, independent of the host note's own padding above.
        // A lone grace note needs a bigger pull-in than either note of a pair.
        tickable.getModifiers?.().forEach((modifier: unknown) => {
          if (modifier.getCategory?.() !== 'gracenotegroups') return;
          const graceNotes = modifier.getGraceNotes?.() ?? [];
          const paddingRight = graceNotes.length === 1 ? -8 : -4;
          graceNotes.forEach((graceNote: unknown) => {
            graceNote.paddingRight = paddingRight;
          });
        });
      }
    }
  }
  const visible = measures.filter((m: unknown) => m?.isVisible());
  if (visible.length === 0) {
    return originalFn.call(this, measures);
  }

  // Replicate the *exact* maxStaffEntries logic from the original method
  // (only updates for ImplicitMeasure — matches the TODO-noted quirk).
  let maxStaffEntries = visible[0].staffEntries.length;
  for (const measure of visible) {
    if (measure?.parentSourceMeasure?.ImplicitMeasure) {
      maxStaffEntries = Math.max(measure.staffEntries.length, maxStaffEntries);
    }
  }

  const staffEntryFactor = 0.3; // matches the original method's local const
  const bonus = maxStaffEntries * staffEntryFactor;

  const prevAddend = this.rules.VoiceSpacingAddendVexflow;
  this.rules.VoiceSpacingAddendVexflow = prevAddend - bonus; // cancels the term algebraically
  const result = originalFn.call(this, measures);
  this.rules.VoiceSpacingAddendVexflow = prevAddend; // always restore, even on throw
  return result;
};

const { createToast } = useToast();
const styles = getComputedStyle(document.documentElement);

interface BeatUnitFraction {
  numerator: number;
  denominator: number;
}
type TempoBeatUnitString = "quarter" | "eighth"
export type MeasureTimingSegment = {
  measureIndex: number;
  startMs: number;
  durationMs: number;
  endMs: number;
  timeSignature: TimeSignature;
  beatsPerMeasure: number;
};
export type TimingMap = MeasureTimingSegment[];
export type BeatTimingInfo = {
  measureIndex: number;
  beatIndexInMeasure: number;
  beatStartMs: number;
  beatDurationMs: number;
  beatsPerMeasure: number;
};

// Default OSMD construction options for the game view (single horizontal
// staffline, following cursor). Callers pass overrides — e.g. the sections
// editor wants a wrapped, multi-row layout with a title and no cursor.
const DEFAULT_OSMD_OPTIONS: IOSMDOptions = {
  autoResize: false,
  drawTitle: false,
  drawSubtitle: false,
  drawMeasureNumbers: true,
  measureNumberInterval: 1,
  drawCredits: false,
  autoGenerateMultipleRestMeasuresFromRestMeasures: false,
  drawPartNames: false,
  drawTimeSignatures: true,
  defaultColorMusic: styles.getPropertyValue("--color-white").trim(),
  followCursor: true,
  disableCursor: false,
  renderSingleHorizontalStaffline: true,
  // drawingParameters: "compacttight",
};

async function getCreditTitle(xmlString: string) {
  if (xmlString.startsWith("\x50\x4b\x03\x04")) {
    // This is a zip file, unpack it first
    xmlString = await MXLHelper.MXLtoXMLstring(xmlString).then(
      (x: string) => {
        return x;
      },
      (err: unknown) => {
        console.error(err);
        createToast({
          type: 'error',
          message: 'OpenSheetMusicDisplay: Invalid MXL file'
        });
        throw new Error("OpenSheetMusicDisplay: Invalid MXL file");
      }
    );
  }

  let title;

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, "application/xml");

  // Find the <credit> with <credit-type>title</credit-type>
  const creditElements = xmlDoc.querySelectorAll("credit");
  for (const credit of creditElements) {
    const type = credit.querySelector("credit-type");
    const words = credit.querySelector("credit-words");
    if (type?.textContent === "title" && words) {
      title = words.textContent?.trim();
    }
  }
  if (!title && creditElements.length > 0) {
    title = creditElements[0].querySelector("credit-words")?.textContent?.trim();
  }

  // If no title found
  return title;
}

function tempoBeatUnitString2Number(tempoBeatUnitString: TempoBeatUnitString) {
  if (tempoBeatUnitString === 'quarter') {
    return 4;
  } else if (tempoBeatUnitString === 'eighth') {
    return 8;
  } else {
    throw new Error(`Invalid tempo beat unit: ${tempoBeatUnitString}`);
  }
}

function calculateBeatUnitFraction(beatUnitDenominator: number, dotted: boolean): BeatUnitFraction {
  // Calculate beatUnit as a fraction
  // For non-dotted: 1/beatUnitDenominator (e.g., quarter = 1/4, eighth = 1/8)
  // For dotted: multiply by 3/2, so (1/beatUnitDenominator) * (3/2) = 3/(2*beatUnitDenominator)
  if (dotted) {
    return {
      numerator: 3,
      denominator: 2 * beatUnitDenominator
    };
  } else {
    return {
      numerator: 1,
      denominator: beatUnitDenominator
    };
  }
}

function calculateBeatsPerMeasure(
  signature: TimeSignature,
  beatUnit: BeatUnitFraction
): number {
  return (signature.numerator * beatUnit.denominator) / (signature.denominator * beatUnit.numerator);
}

const getMeasureTempoBeatUnit = (
  measure: SourceMeasure,
  measureIndex: number,
  previousBeatUnit?: BeatUnitFraction
): BeatUnitFraction => {
  const firstTempoExpression = (measure as { TempoExpressions?: unknown[] } | undefined)?.TempoExpressions?.[0] as
    | { InstantaneousTempo?: { beatUnit?: string; dotted?: boolean } }
    | undefined;
  const beatUnitString = firstTempoExpression?.InstantaneousTempo?.beatUnit as TempoBeatUnitString | undefined;
  const dotted = !!firstTempoExpression?.InstantaneousTempo?.dotted;

  if (!beatUnitString) {
    if (previousBeatUnit) return previousBeatUnit;
    throw new Error(`Missing tempo beat unit at measure ${measureIndex + 1}`);
  }

  const beatUnitDenominator = tempoBeatUnitString2Number(beatUnitString);
  return calculateBeatUnitFraction(beatUnitDenominator, dotted);
};

const getMeasureBpm = (measure: SourceMeasure, measureIndex: number, previousBpm?: number): number => {
  const firstTempoExpression = (measure as { TempoExpressions?: unknown[] } | undefined)?.TempoExpressions?.[0] as
    | { InstantaneousTempo?: { TempoInBpm?: number } }
    | undefined;
  const nextBpm = firstTempoExpression?.InstantaneousTempo?.TempoInBpm;
  if (nextBpm === undefined || nextBpm === null) {
    if (typeof previousBpm === 'number' && previousBpm > 0) return previousBpm;
    throw new Error(`Missing BPM at measure ${measureIndex + 1}`);
  }
  if (!(typeof nextBpm === 'number' && nextBpm > 0)) {
    throw new Error(`Invalid BPM at measure ${measureIndex + 1}`);
  }
  return nextBpm;
};

const getMeasureTimeSignature = (
  measure: SourceMeasure,
  measureIndex: number,
  previousSignature?: TimeSignature
): TimeSignature => {
  const active = (measure as { ActiveTimeSignature?: { Numerator?: number; Denominator?: number } } | undefined)?.ActiveTimeSignature;
  if (!active?.Numerator || !active?.Denominator) {
    if (previousSignature) return previousSignature;
    throw new Error(`Missing time signature at measure ${measureIndex + 1}`);
  }
  return {
    numerator: active.Numerator,
    denominator: active.Denominator,
  };
};

const buildMeasureTimingMap = (measures: SourceMeasure[]): TimingMap => {
  const segments: TimingMap = [];
  let runningStartMs = 0;
  let runningTimeSignature: TimeSignature | undefined;
  let runningBpm: number | undefined;
  let runningBeatUnit: BeatUnitFraction | undefined;

  for (let measureIndex = 0; measureIndex < measures.length; measureIndex++) {
    const measure = measures[measureIndex];
    const nextSignature = getMeasureTimeSignature(measure, measureIndex, runningTimeSignature);
    const nextBpm = getMeasureBpm(measure, measureIndex, runningBpm);
    const nextBeatUnit = getMeasureTempoBeatUnit(measure, measureIndex, runningBeatUnit);
    // Keep measure duration driven by tempo expression beat-unit,
    // but expose beatsPerMeasure as notated beats (time-signature numerator)
    // so metronome ticks every written beat (e.g. 8/8 -> 8 ticks/bar).
    const tempoBeatsPerMeasure = calculateBeatsPerMeasure(nextSignature, nextBeatUnit);
    const durationMs = tempoBeatsPerMeasure * (60000 / nextBpm);
    const beatsPerMeasure = nextSignature.numerator;

    const segment: MeasureTimingSegment = {
      measureIndex,
      startMs: runningStartMs,
      durationMs,
      endMs: runningStartMs + durationMs,
      timeSignature: nextSignature,
      beatsPerMeasure,
    };
    segments.push(segment);

    runningStartMs = segment.endMs;
    runningTimeSignature = nextSignature;
    runningBpm = nextBpm;
    runningBeatUnit = nextBeatUnit;
  }

  return segments;
};

// Parse-only validation of a MusicXML string's timing metadata, for gating an
// upload before the user invests further steps. Runs the *same*
// buildMeasureTimingMap the game path runs, on a throwaway OSMD over a detached
// div — a hand-rolled tempo scan would drift from OSMD's interpretation and let
// files through that fail later. load() parses without touching layout, so the
// div is never attached and never rendered.
export const validateMusicXmlTiming = async (xml: string): Promise<void> => {
  const probe = new OpenSheetMusicDisplay(document.createElement('div'), {
    ...DEFAULT_OSMD_OPTIONS,
    disableCursor: true,
    followCursor: false,
  });
  await probe.load((await preprocessMusicXML(xml)).xml);
  const measures = probe.Sheet?.SourceMeasures;
  if (!measures?.length) {
    throw new Error('No measures found in sheet music');
  }
  buildMeasureTimingMap(measures);
};

// Get the name of a note from the OSMD Note object
const getNoteName = (note: OSMDNote): string => {
  const noteName = NoteEnum[note.Pitch.FundamentalNote];
  // In OSMD, the octave is offset by 3 compared to standard notation
  const octave = note.Pitch.Octave + 3;
  return `${noteName}${octave}`;
};

const isCrossNotehead = (notehead?: string): boolean => {
  return notehead === 'X';
};

// Drum-notation pitch + notehead → drum kit pieces. The (note → drum)
// relationship is a notation standard; the drum → midi numbers indirection
// is user-editable and comes from useMidiNoteMapping.
const noteToDrumKeys = (noteName: string, notehead?: string): DrumKey[] => {
  const isX = isCrossNotehead(notehead);
  switch (noteName) {
    case 'D4': return ['pedalHiHat'];
    case 'F4': return ['bassDrum'];
    case 'C5': return [isX ? 'snareRim' : 'snareDrum'];
    case 'G5': return [isX ? 'closedHiHat' : 'openHiHat'];
    case 'E5': return ['highTom'];
    case 'D5': return ['midTom'];
    case 'A4': return ['lowFloorTom', 'highFloorTom'];
    case 'A5': return ['crashCymbal'];
    case 'F5': return ['rideCymbal'];
    default:
      console.log(`Unknown note mapping for: ${noteName}`);
      return [];
  }
};

// How many strikes a note stands for. A measured single-note tremolo notates one
// note played as a run of shorter, equal notes; the note's own beams count toward
// that subdivision alongside the stroke count — matching MuseScore, which writes
// these files — so the repeated value is a 1/(4 · 2^(beams + strokes)) note and a
// 16th (2 beams) with one stroke is a run of 32nds. Everything else is a single
// strike, including buzz rolls, which are a press rather than countable strokes.
const getTremoloRepeatCount = (note: OSMDNote): number => {
  const tremolo = note.TremoloInfo;
  if (!tremolo?.tremoloStrokes) return 1;
  if (tremolo.tremoloUnmeasured || tremolo.tremoloBetweenNotesStart || tremolo.tremoloBetweenNotesStop) return 1;

  // Beams come from the written <type> duration rather than note.NoteTypeXml:
  // OSMD's type-name table misspells "eighth" as "eigth", so NoteTypeXml reads
  // UNDEFINED for every eighth note — the most common tremolo carrier there is.
  // TypeLength drops dots and tuplet scaling, so the halvings divide evenly.
  const typeValue = note.TypeLength?.RealValue;
  if (!typeValue) return 1;
  const beams = Math.max(0, Math.round(Math.log2(0.25 / typeValue)));

  const repeatedValue = 0.25 / 2 ** (beams + tremolo.tremoloStrokes);
  return Math.max(1, Math.floor(note.Length.RealValue / repeatedValue));
};

// Grace notes share the main note's Timestamp (no notated duration), so OSMD lists
// them as consecutive VoiceEntries right before it. Group by ParentVoice first, since
// a staff entry can interleave multiple voices.
//
// Example: a 3-grace-note run leading into a main note hit at 1000ms, with the
// default 40ms step, plays at 880ms, 920ms, 960ms, then the main note at 1000ms
// (each grace note GRACE_NOTE_OFFSET_MS earlier than the one after it).
//
// A run with no following main note (GraceAfterMainNote, e.g. trailing a measure) is
// left unoffset — there's nothing to borrow time from.
//
// availableGapMs caps how far a run can reach back. Example: that same 3-note run
// but only 60ms free before it (availableGapMs = 60) shrinks its step to
// (60 - GRACE_RUN_MIN_GAP_MS) / 3 = 15ms, so it plays at 955ms, 970ms, 985ms instead
// of overflowing into the previous note. GRACE_RUN_MIN_GAP_MS is that 15ms reserved
// buffer, keeping the earliest grace note outside highlightNote's 10ms
// "simultaneous hit" window around the previous note.
const GRACE_RUN_MIN_GAP_MS = 15;
const computeGraceOffsetsMs = <T extends { IsGrace: boolean; GraceAfterMainNote: boolean; ParentVoice: unknown }>(
  voiceEntries: T[],
  availableGapMs: number | undefined,
): Map<T, number> => {
  const offsetMsByVoiceEntry = new Map<T, number>();
  const entriesByVoice = new Map<unknown, T[]>();
  for (const voiceEntry of voiceEntries) {
    const entries = entriesByVoice.get(voiceEntry.ParentVoice);
    if (entries) entries.push(voiceEntry);
    else entriesByVoice.set(voiceEntry.ParentVoice, [voiceEntry]);
  }

  for (const entries of entriesByVoice.values()) {
    let index = 0;
    while (index < entries.length) {
      if (!entries[index].IsGrace) {
        index++;
        continue;
      }
      let runEnd = index;
      while (runEnd < entries.length && entries[runEnd].IsGrace) {
        runEnd++;
      }
      const leadsIntoMainNote = runEnd < entries.length && !entries[index].GraceAfterMainNote;
      if (leadsIntoMainNote) {
        const runLength = runEnd - index;
        const stepMs = availableGapMs != null
          ? Math.max(0, Math.min(GRACE_NOTE_OFFSET_MS, (availableGapMs - GRACE_RUN_MIN_GAP_MS) / runLength))
          : GRACE_NOTE_OFFSET_MS;
        for (let graceIndex = index; graceIndex < runEnd; graceIndex++) {
          const positionsBeforeMainNote = runEnd - graceIndex;
          offsetMsByVoiceEntry.set(entries[graceIndex], -stepMs * positionsBeforeMainNote);
        }
      }
      index = runEnd;
    }
  }
  return offsetMsByVoiceEntry;
};

// The notehead colour for each settled status, resolved once per theme.
//
// `styles` is a live CSSStyleDeclaration, so each getPropertyValue on it flushes
// pending style computation. In the hit path that flush lands mid-frame, right
// after the scroll transform was written — a forced synchronous recalc on every
// note. Reading them together off the theme watcher keeps the hot path to a
// single property access.
//
// Unplayed is the plain notehead. Not --color-text-primary: white and black are
// the base everywhere else, and recolorOsmdByTheme only knows how to swap those
// two when the theme changes.
type ThemeColors = Record<HitStatus, string>;

const readThemeColors = (theme: string): ThemeColors => {
  const read = (name: string) => styles.getPropertyValue(name).trim();
  return {
    [HitStatus.Unplayed]: read(theme === 'dark' ? '--color-white' : '--color-black'),
    [HitStatus.Perfect]: read('--color-hit-perfect'),
    [HitStatus.Good]: read('--color-hit-good'),
    [HitStatus.Missed]: read('--color-hit-missed'),
  };
};

// How bad each outcome is, for picking the one a shared notehead should show.
const HIT_SEVERITY: Record<HitStatus, number> = {
  [HitStatus.Unplayed]: 0,
  [HitStatus.Perfect]: 1,
  [HitStatus.Good]: 2,
  [HitStatus.Missed]: 3,
};

// The status a notehead shows for the notes sharing it: the worst of them, but only
// once that worst is settled. Missed is terminal — nothing outranks it — so it lands
// the moment a stroke is dropped, while any better outcome waits for every stroke to
// resolve. Otherwise a clean first stroke would flash green with the roll still to
// come, and a later fumble would have to walk it back.
const getNoteheadStatus = (group: Note[]): HitStatus => {
  let worst = HitStatus.Unplayed;
  let allResolved = true;

  for (const note of group) {
    const status = note.hitStatus ?? HitStatus.Unplayed;
    if (status === HitStatus.Unplayed) allResolved = false;
    else if (HIT_SEVERITY[status] > HIT_SEVERITY[worst]) worst = status;
  }

  if (worst === HitStatus.Missed) return worst;
  return allResolved ? worst : HitStatus.Unplayed;
};

const getDirectionRelativeX = (direction: Element): number => {
  const relativeX = parseFloat(direction.querySelector('words')?.getAttribute('relative-x') ?? '0');
  return Number.isNaN(relativeX) ? 0 : relativeX;
};

// Label key shared with processNotes: measure + onset within the measure in
// whole notes. Matching by onset (rather than counting notes in document order)
// stays correct across multiple voices, where MusicXML lists each voice
// back-to-back via <backup> but OSMD walks them interleaved in time order.
const getNoteLabelKey = (measureIndex: number, onsetWholeNotes: number): string =>
  `${measureIndex}:${Math.round(onsetWholeNotes * 10000)}`;

// Onset (whole notes from measure start) of each <note> in a measure, tracking
// <backup>/<forward>/<chord>. `divisions` is carried across measures.
const computeNoteOnsets = (children: Element[], state: { divisions: number }): Map<Element, number> => {
  const onsets = new Map<Element, number>();
  const readDuration = (el: Element) => parseFloat(el.querySelector('duration')?.textContent ?? '0') || 0;
  let position = 0;
  let lastOnset = 0;

  children.forEach(child => {
    if (child.tagName === 'attributes') {
      const divisions = parseFloat(child.querySelector('divisions')?.textContent ?? '');
      if (divisions > 0) state.divisions = divisions;
    } else if (child.tagName === 'backup') {
      position -= readDuration(child);
    } else if (child.tagName === 'forward') {
      position += readDuration(child);
    } else if (child.tagName === 'note') {
      const isChord = child.querySelector('chord') !== null;
      const onset = isChord ? lastOnset : position;
      lastOnset = onset;
      onsets.set(child, onset / (state.divisions * 4));
      if (!isChord && child.querySelector('grace') === null) position += readDuration(child);
    }
  });

  return onsets;
};

const distributeNoteLabelDirections = (xmlDoc: XMLDocument): Map<string, NoteLabel> => {
  const labelByNoteKey = new Map<string, NoteLabel>();
  const onsetState = { divisions: 1 };

  xmlDoc.querySelectorAll('measure').forEach((measure, measureIndex) => {
    const children = Array.from(measure.children);
    const onsets = computeNoteOnsets(children, onsetState);
    const pending: Element[] = [];
    let index = 0;

    const takeLeftmostPending = (): Element | undefined => {
      pending.sort((a, b) => getDirectionRelativeX(a) - getDirectionRelativeX(b));
      return pending.shift();
    };

    while (index < children.length) {
      const element = children[index];

      if (element.tagName === 'direction') {
        if (element.querySelector('words')) {
          pending.push(element);
        }
        // directions without words (metronome, dynamics, etc.) are left exactly where they
        // already are in the document — they don't need repositioning by this algorithm
        index++;
        continue;
      }

      if (element.tagName !== 'note') {
        index++;
        continue;
      }

      const isRest = element.querySelector('rest') !== null;
      if (isRest) {
        index++;
        continue;
      }

      const isGrace = element.querySelector('grace') !== null;

      if (pending.length > 0) {
        const direction = takeLeftmostPending();
        const words = direction?.querySelector('words');
        const text = words?.textContent?.trim();
        // Stripped entirely rather than repositioned next to its note — OSMD never
        // sees it (so it never reserves layout space for it); processNotes
        // re-attaches this same text to the rendered note by matching this exact
        // (measureIndex, onset) key, and draws it after render instead (see
        // drawNoteLabel). font-size/font-family carry over too, so the redrawn
        // label matches whatever size the source MusicXML actually specified
        // instead of one derived from the notehead.
        const noteKey = getNoteLabelKey(measureIndex, onsets.get(element) ?? 0);
        if (text && !labelByNoteKey.has(noteKey)) {
          labelByNoteKey.set(noteKey, {
            text,
            fontSize: words?.getAttribute('font-size') ?? undefined,
            fontFamily: words?.getAttribute('font-family') ?? undefined,
          });
        }
        // Remove only the <direction-type> that held the words, not the whole
        // <direction> — some exporters (seen from MuseScore) leave an empty
        // <words> placeholder as a sibling direction-type on the same
        // <direction> as a tempo <metronome>/<sound tempo>, and removing the
        // entire element would silently delete that tempo too.
        const directionType = words?.closest('direction-type');
        directionType?.remove();
        if (direction && direction.childElementCount === 0) {
          direction.remove();
        }
      }

      if (isGrace) {
        while (
          index < children.length &&
          children[index].tagName === 'note' &&
          children[index].querySelector('grace') !== null
        ) {
          index++;
        }
      } else {
        index++;
      }
    }
  });

  return labelByNoteKey;
};

const preprocessMusicXML = async (xmlString: string) => {
  // Force rendering 5 staff lines.  If the clef sign is percussion, it might treat it as single line percussion without bass drum pointing up
  if (xmlString.startsWith("\x50\x4b\x03\x04")) {
    // This is a zip file, unpack it first
    xmlString = await MXLHelper.MXLtoXMLstring(xmlString).then(
      (x: string) => {
        return x;
      },
      (err: unknown) => {
        console.error(err);
        createToast({
          type: 'error',
          message: 'OpenSheetMusicDisplay: Invalid MXL file'
        });
        throw new Error("OpenSheetMusicDisplay: Invalid MXL file");
      }
    );
  }

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, "application/xml");

  // Remove explicitly positioned text that does not align with any note in its
  // measure. Words without default-x, such as sticking text like "L*", remain.
  xmlDoc.querySelectorAll('measure').forEach((measure) => {
    const noteXPositions = Array.from(measure.querySelectorAll('note'))
      .map((note) => parseFloat(note.getAttribute('default-x') ?? ''))
      .filter((x) => !Number.isNaN(x));

    measure.querySelectorAll('words[default-x]').forEach((words) => {
      const wordsX = parseFloat(words.getAttribute('default-x') ?? '');
      if (Number.isNaN(wordsX)) return;
      if (!noteXPositions.some((noteX) => Math.abs(noteX - wordsX) <= 1)) {
        words.remove();
      }
    });
  });

  const labelByNoteKey = distributeNoteLabelDirections(xmlDoc);
  tagDecoratedNoteheads(xmlDoc);

  // Find the <credit> with <credit-type>title</credit-type>
  const clefSign = xmlDoc.querySelector("sign");
  if (clefSign) {
    clefSign.textContent = "G";
  }

  // OSMD doesn't recognize <notehead smufl="noteheadCircleX">other</notehead>,
  // rewrite to the form it understands: <notehead>circle-x</notehead>.
  const rewriteSmuflNotehead = (smufl: string, shape: string) => {
    xmlDoc.querySelectorAll(`notehead[smufl="${smufl}"]`).forEach(notehead => {
      notehead.removeAttribute('smufl');
      notehead.textContent = shape;
    });
  };
  rewriteSmuflNotehead('noteheadCircleX', 'circle-x');
  rewriteSmuflNotehead('noteheadDiamondBlackOld', 'diamond');

  // MuseScore pins some rests to a staff line with <display-step>/<display-octave>,
  // so otherwise-identical rests sit at different heights depending on the source
  // file. Rather than just clearing that info (which leaves multi-voice rests to
  // collide, since OSMD picks the same default line for every voice), we
  // re-assign it ourselves: one fixed vertical "slot" per voice number, so rests
  // in different voices never land on top of each other, and rests within the
  // same voice are always level with each other regardless of duration type.
  //
  // Slots are staff positions (display-step/display-octave), voice 1 pinned
  // above the middle line, voice 2 below it, voice 3/4 further out. Extend this
  // map if you use more than 4 voices per staff.
  const REST_POSITIONS: Record<string, [string, string]> = {
    "1": ["D", "5"], // space above the middle line
    "2": ["F", "4"], // space below the middle line
    "3": ["F", "5"], // top line
    "4": ["E", "4"], // bottom line
  };
  const DEFAULT_REST_POSITION: [string, string] = ["B", "4"]; // centered fallback

  const getCollidingVoiceSet = (measure: Element): Set<string> => {
    const xToVoices = new Map<string, Set<string>>();
    measure.querySelectorAll('note').forEach((note) => {
      const rest = note.querySelector('rest');
      if (!rest) return;

      const voice = note.querySelector('voice')?.textContent?.trim() ?? '1';
      const defaultX = note.getAttribute('default-x');
      if (!defaultX) return;

      const voices = xToVoices.get(defaultX) ?? new Set<string>();
      voices.add(voice);
      xToVoices.set(defaultX, voices);
    });

    const collidingVoices = new Set<string>();
    xToVoices.forEach((voices) => {
      if (voices.size > 1) {
        voices.forEach((voice) => collidingVoices.add(voice));
      }
    });

    return collidingVoices;
  };

  xmlDoc.querySelectorAll('measure').forEach((measure) => {
    const collidingVoices = getCollidingVoiceSet(measure);

    measure.querySelectorAll('rest').forEach(rest => {
      // Leave whole-measure rests alone; they're centered regardless of voice
      // and repositioning them can visually detach them from the measure.
      if (rest.getAttribute('measure') === 'yes') {
        rest.replaceChildren();
        return;
      }

      const note = rest.parentElement; // <rest> is always a child of <note>
      if (note?.getAttribute('print-object') === 'no') {
        return;
      }

      const voice = note?.querySelector('voice')?.textContent?.trim() ?? '1';
      const [step, octave] = collidingVoices.has(voice)
        ? (REST_POSITIONS[voice] ?? DEFAULT_REST_POSITION)
        : DEFAULT_REST_POSITION;

      rest.replaceChildren();
      const stepEl = xmlDoc.createElement('display-step');
      stepEl.textContent = step;
      const octaveEl = xmlDoc.createElement('display-octave');
      octaveEl.textContent = octave;
      rest.appendChild(stepEl);
      rest.appendChild(octaveEl);
    });
  });

  // MuseScore (and other authoring tools) only records an articulation's vertical
  // side as layout coordinates (default-y), not the MusicXML `placement` attribute
  // that OSMD's ArticulationReader actually reads — so without an explicit
  // `placement`, every such articulation falls back to OSMD/VexFlow's own
  // above/below heuristic (opposite the stem), ignoring where the source file
  // actually drew it. Converting default-y's sign into an explicit `placement`
  // (MusicXML y-axis: positive is up) lets renderMusicXml's
  // `ArticulationPlacementFromXML` rule honor that original layout instead.
  xmlDoc.querySelectorAll('articulations').forEach((articulations) => {
    Array.from(articulations.children).forEach((articulation) => {
      if (articulation.hasAttribute('placement')) return;
      const defaultY = parseFloat(articulation.getAttribute('default-y') ?? '');
      if (Number.isNaN(defaultY)) return;
      articulation.setAttribute('placement', defaultY >= 0 ? 'above' : 'below');
    });
  });

  // Serialize back to string
  const serializer = new XMLSerializer();
  const modifiedXmlString = serializer.serializeToString(xmlDoc);

  return { xml: modifiedXmlString, labelByNoteKey };
};

// The stave prototype patch mutates VexFlow's global Stave prototype, so it
// must run exactly once regardless of how many useSheetMusic instances exist.
// A second run would re-wrap the already-wrapped methods (e.g. setNoteStartX
// would subtract 18px twice), compounding the layout offsets.
let stavePrototypePatched = false;

// reference: https://github.com/0xfe/vexflow/blob/master/src/stave.ts
const patchStavePrototype = async (osmd: OpenSheetMusicDisplay) => {
  if (stavePrototypePatched) return;

  await osmd.load('/sample-bar.mxl');
  osmd.render()

  const firstMeasure = osmd.GraphicSheet.MeasureList?.[0]?.[0]

  if (!firstMeasure) return

  const modifiers = firstMeasure.staffEntries
    .flatMap(entry => entry.graphicalVoiceEntries ?? [])
    .flatMap(entry => entry.notes ?? [])
    .filter(note => note instanceof VexFlowGraphicalNote)
    .flatMap(note => (note.vfnote?.[0] as (Flow.StemmableNote & {
      getModifiers(): (Flow.Modifier & { getAttribute(name: string): string })[];
    }) | undefined)?.getModifiers() ?? []);

  const tremolo = modifiers.find(modifier => modifier.getAttribute('type') === 'Tremolo');
  const dot = modifiers.find(modifier => modifier.getCategory() === 'dots');

  if (!tremolo) {
    console.error('Tremolo modifier not found');
    return;
  }
  if (!dot) {
    throw new Error('Sample bar contains no dot to patch.');
  }

  const DotClass = dot.constructor as typeof dot.constructor & {
    prototype: Flow.Dot;
    format: (dots: Flow.Dot[] | undefined, state: {
      left_shift: number; right_shift: number; text_line: number;
    }) => boolean;
  };
  const originalDotFormat = DotClass.format;
  const originalDotDraw = DotClass.prototype.draw;
  const dotDrawingWidth = dot.getWidth();

  DotClass.format = function (dots, state) {
    for (const dot of dots ?? []) {
      if (dot.getNote().getCategory() === 'stavenotes') {
        dot.setWidth(-5);
      }
    }
    return originalDotFormat.call(this, dots, state);
  };

  DotClass.prototype.draw = function () {
    if (this.getNote().getCategory() !== 'stavenotes') {
      return originalDotDraw.call(this);
    }
    const layoutWidth = this.getWidth();
    this.setWidth(dotDrawingWidth);
    try {
      return originalDotDraw.call(this);
    } finally {
      this.setWidth(layoutWidth);
    }
  };

  // const TremoloProto = tremolo.constructor.prototype;

  // draw() reads the stroke offsets off the class itself (`Tremolo.YOFFSETSTEMUP`)
  // on every call, so redefining the static getter is enough to move them. Stock -9
  // sits the strokes too close to the notehead on stem-up notes.
  Object.defineProperty(tremolo.constructor, 'YOFFSETSTEMUP', {
    get: () => 1,
    configurable: true,
  });

  Object.defineProperty(tremolo.constructor, 'YOFFSETSTEMDOWN', {
    get: () => -30,
    configurable: true,
  });


  const StaveProto = firstMeasure.stave.constructor.prototype

  // Patch setNoteStartX — called during stave.format() which runs before formatVoices
  const origSetNoteStartX = StaveProto.setNoteStartX;
  StaveProto.setNoteStartX = function(x: number) {
    return origSetNoteStartX.call(this, x - 18);
  };

  const origGetModifiers = StaveProto.getModifiers;
  StaveProto.getModifiers = function(position?: number, category?: string) {
    const modifiers = origGetModifiers.call(this, position, category);
    modifiers.forEach((modifier: { attrs: { type: string; }; width: number; }) => {
      if (modifier.attrs.type === 'Barline') {
        if (modifier.width) {
          modifier.width = -10;
        }
      }
    })
    return modifiers;
  };

  const origSetWidth = StaveProto.setWidth;
  StaveProto.setWidth = function (width: number) {
    return origSetWidth.call(this, width);
  };

  // Patch getNoteEndX — read by Formatter.formatToStave as (end_x - start_x - 10)
  const origGetNoteEndX = StaveProto.getNoteEndX;
  StaveProto.getNoteEndX = function () {
    const val = origGetNoteEndX.call(this);
    return val; // reclaim the 10px formatToStave subtracts
  };

  stavePrototypePatched = true;
}

export type SheetMusic = ReturnType<typeof useSheetMusic>;

export const SHEET_MUSIC_KEY = Symbol() as InjectionKey<SheetMusic>;

export function useInjectSheetMusic() {
  const sheetMusic = inject(SHEET_MUSIC_KEY);
  if (!sheetMusic) {
    throw new Error('useSheetMusic must be provided via provide(SHEET_MUSIC_KEY)');
  }
  return sheetMusic;
}

export function provideSheetMusic() {
  const sheetMusic = useSheetMusic();
  provide(SHEET_MUSIC_KEY, sheetMusic);
  return sheetMusic;
}

export function useSheetMusic() {
  const settings = useSettings();
  const { getMidisForDrum } = useMidiNoteMapping();

  // shallowRef, not ref: a deep ref proxies the whole OSMD/VexFlow object graph,
  // so every internal property read during layout goes through a reactive trap —
  // ~6.3s of render on a 130-measure score vs ~0.35s unwrapped.
  const osmd = shallowRef<OpenSheetMusicDisplay>();
  const container = shallowRef<HTMLElement>();
  const isLoaded = ref(false);
  const isLoading = ref(false);
  const timeSignature = ref<TimeSignature>({
    numerator: 4,
    denominator: 4
  });
  // Initial display defaults from the first available tempo expression.
  // Playback timing should use measureTimingMap/getBeatAtTime.
  const initialBpm = ref(120);
  // shallowRef: the map is only ever replaced wholesale, and a deep ref would
  // proxy every segment read in getRawMeasureAtTime/getBeatAtTime — both run
  // per frame. Replace the array to update; never mutate or push in place.
  const measureTimingMap = shallowRef<TimingMap>([]);
  const error = ref<string | null>(null);
  const currentMeasureIndex = ref(0);
  const totalMeasures = ref(0);
  // Deep ref, deliberately: played/hitStatus/x are mutated per-note in place and
  // rendered reactively. Heavy nested values must be markRaw'd at construction.
  const notes = ref<Note[]>([]);
  const songTitle = ref("");
  // The raw, as-loaded MusicXML — the only XML state anything outside this
  // composable needs (to check "is something loaded" or to reset for a new
  // upload). The preprocessed string actually fed to OSMD is a pure, cacheable
  // derivative of this and has no reason to be reactive or exposed — see
  // lastPreprocessed below.
  const xmlContent = ref("");
  // preprocessMusicXML is a pure function of its input string — a mode switch
  // (single/multi) never changes its output, only OSMD's own render options do.
  // Caches the last (source, result) pair so renderMusicXml can skip
  // re-parsing/re-mutating the XML DOM on a mode-only re-render.
  let lastPreprocessed: { source: string; result: string; labelByNoteKey: Map<string, NoteLabel> } | null = null;
  const renderMode = ref<'single' | 'multi'>('single');
  // Bumped by renderOsmd() to invalidate geometry derived from OSMD internals.
  const renderVersion = ref(0);
  const themeColors = shallowRef(readThemeColors(settings.currentTheme.value));

  // Dispatch — call once per note after render, and again from the theme watcher
  // (overlays don't survive a bare recolor pass the way SVG fills do).
  const applyNoteheadDecoration = (note: Note): void => {
    const graphicalNote = note.graphicalNote as VexFlowGraphicalNote | undefined;
    if (!graphicalNote || (!note.decoration && !note.label)) return;
    const color = themeColors.value[HitStatus.Unplayed];

    if (note.decoration === 'rimshot') {
      console.log("DRAW RIMSHOT SLASH");
      drawRimshotSlash(graphicalNote, color);
    } else if (note.decoration === 'ghost') {
      drawGhostParentheses(graphicalNote, color);
    }

    if (note.label) {
      drawNoteLabel(graphicalNote, note.label, color);
    }
  };

  const getTimingLeadInMs = () => {
    return measureTimingMap.value[0]?.durationMs ?? 0;
  };

  const songDuration = computed(() => {
    const lastNoteTs = notes.value.at(-1)?.timestamp ?? 0;
    const lastSegmentEnd = (measureTimingMap.value.at(-1)?.endMs ?? 0) + getTimingLeadInMs();
    return Math.max(lastNoteTs, lastSegmentEnd);
  });

  const staffHeight = computed(() => {
    // Recompute after every render — osmd is a shallowRef, so its internal
    // mutations aren't tracked; renderOsmd()'s version bump is the trigger.
    renderVersion.value;
    return (osmd.value?.GraphicSheet?.MusicPages[0]?.MusicSystems[0]?.PositionAndShape.Size.height ?? 4) * (osmd.value?.zoom ?? 1) * 10;
  });

  const measureHeight = computed(() => {
    // Recompute after every render — osmd is a shallowRef, so its internal
    // mutations aren't tracked; renderOsmd()'s version bump is the trigger.
    renderVersion.value;
    return 20 * (osmd.value?.zoom ?? 1);
    // return (osmd.value?.GraphicSheet?.MeasureList?.[0]?.[0]?.PositionAndShape.Size.height ?? 4) * (osmd.value?.zoom ?? 1) * 10;
  });

  const updateMeasureVisibility = () => {
    if (renderMode.value === 'multi') return;
    const containerEl = container.value;
    if (!containerEl) return;

    const osmdSvgPage1 = containerEl.querySelector('#osmdSvgPage1') as HTMLElement | null;
    if (!osmdSvgPage1) return;

    const currentMeasureNumber = currentMeasureIndex.value + 1;
    const minVisibleMeasure = Math.max(1, currentMeasureNumber - 4);
    const maxVisibleMeasure = currentMeasureNumber + 5;

    const measureGroups = osmdSvgPage1.querySelectorAll<SVGGElement>('g.vf-measure');
    measureGroups.forEach((measureGroup) => {
      const id = parseInt(measureGroup.getAttribute('id') ?? '', 10);
      if (Number.isNaN(id)) {
        measureGroup.style.display = '';
        return;
      }
      measureGroup.style.display = id >= minVisibleMeasure && id <= maxVisibleMeasure ? '' : 'none';
    });
  };

  const showAllMeasures = () => {
    const containerEl = container.value;
    if (!containerEl) return;

    const osmdSvgPage1 = containerEl.querySelector('#osmdSvgPage1') as HTMLElement | null;
    if (!osmdSvgPage1) return;

    const measureGroups = osmdSvgPage1.querySelectorAll<SVGGElement>('g.vf-measure');
    measureGroups.forEach((measureGroup) => {
      measureGroup.style.display = '';
    });
  };

  const applyRenderMode = (mode: 'single' | 'multi') => {
    if (!osmd.value) return;
    renderMode.value = mode;
    osmd.value.setOptions({
      renderSingleHorizontalStaffline: mode === 'single',
    });
  };

  // Initialize OSMD. Pass optionOverrides to diverge from the game defaults.
  // renderSingleHorizontalStaffline there is what picks the initial mode —
  // see applyRenderMode for the EngravingRules flag that follows it on every
  // render, including later calls to setRenderMode.
  const initOSMD = async (
    element: HTMLElement,
    optionOverrides: Partial<IOSMDOptions> = {},
  ) => {
    container.value = element;
    const options: IOSMDOptions = { ...DEFAULT_OSMD_OPTIONS, ...optionOverrides };
    renderMode.value = options.renderSingleHorizontalStaffline === false ? 'multi' : 'single';
    osmd.value = new OpenSheetMusicDisplay(container.value, options);
    await patchStavePrototype(osmd.value);
    return osmd.value;
  };

  // Render the current OSMD instance, optionally at a new zoom. Always go through
  // this rather than calling osmd.render() directly: osmd is a shallowRef, so the
  // version bump is what lets derived geometry (staffHeight) recompute.
  const renderOsmd = (zoom?: number) => {
    if (!osmd.value) return;
    if (zoom != null) osmd.value.zoom = zoom;
    osmd.value.render();
    recolorOsmdByTheme(container.value, settings.currentTheme.value);
    notes.value.forEach(applyNoteheadDecoration);
    renderVersion.value++;
  };

  // Render MusicXML into the OSMD instance: rules → preprocess → load → zoom → render.
  // Shared by loadMusicXML and standalone consumers (e.g. the sections editor) that want
  // the notation without game note-extraction. Pass zoom to override OSMD's default.
  //
  // Rules must be set before load(): PlaceWordsInsideStafflineFromXml gates whether the
  // parser reads default-y off <words> at all, so setting it after leaves every expression
  // without the value layout needs. It persists on the instance, so a later load masks the
  // bug — the first load on a fresh OSMD is the one that places words wrong.
  const renderMusicXml = async (xml: string, zoom?: number, mode: 'single' | 'multi' = renderMode.value) => {
    if (!osmd.value) return;
    xmlContent.value = xml;
    applyRenderMode(mode);
    osmd.value.EngravingRules.RenderClefsAtBeginningOfStaffline = false;
    osmd.value.EngravingRules.PlaceWordsInsideStafflineFromXml = true;
    osmd.value.EngravingRules.PlaceWordsInsideStafflineYOffset = -1;
    osmd.value.EngravingRules.TupletNumberLimitConsecutiveRepetitions = false;
    osmd.value.EngravingRules.MetronomeMarkYShift = -3;

    if (lastPreprocessed?.source !== xml) {
      const processed = await preprocessMusicXML(xml);
      lastPreprocessed = { source: xml, result: processed.xml, labelByNoteKey: processed.labelByNoteKey };
    }
    await osmd.value.load(lastPreprocessed.result);
    renderOsmd(zoom);
  };

  // Load MusicXML for gameplay: render the notation, then extract song info + notes.
  const loadMusicXML = async (zoom?: number) => {
    if (!osmd.value) return;

    isLoading.value = true;
    isLoaded.value = false;
    error.value = null;
    try {
      await renderMusicXml(xmlContent.value, zoom, renderMode.value);
      await extractSongInfo();
      await processNotes();

      isLoaded.value = true;
      isLoading.value = false;

      return true;
    } catch (err: unknown) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Failed to load MusicXML';
      error.value = message;
      createToast({
        type: 'error',
        message,
      });
      isLoaded.value = false;
      isLoading.value = false;
      return false;
    }
  };

  // Extract song information from the loaded music
  const extractSongInfo = async () => {
    if (!osmd.value || !osmd.value.Sheet) return;

    const sheet = osmd.value.Sheet;
    totalMeasures.value = sheet.SourceMeasures.length;
    // Get title
    const creditTitle = await getCreditTitle(xmlContent.value);
    if (creditTitle) {
      songTitle.value = creditTitle
        ;
    } else if (sheet.TitleString) {
      songTitle.value = sheet.TitleString;
    }

    measureTimingMap.value = buildMeasureTimingMap(sheet.SourceMeasures);
    const firstSegment = measureTimingMap.value[0];

    if (firstSegment) {
      timeSignature.value = firstSegment.timeSignature;
      const firstMeasure = sheet.SourceMeasures[0];
      initialBpm.value = getMeasureBpm(firstMeasure, 0, undefined);
    } else {
      throw new Error('No measures found in sheet music');
    }
  };

  const getPossibleMidiNumbers = (noteName: string, notehead?: string): number[] => {
    return noteToDrumKeys(noteName, notehead).flatMap((drum) => getMidisForDrum(drum));
  };

  // Process notes for gameplay
  const processNotes = async () => {
    if (!osmd.value || !osmd.value.Sheet) return;

    const allNotes: Note[] = [];
    const measures = osmd.value.Sheet.SourceMeasures;
    const timingMap = measureTimingMap.value.length === measures.length
      ? measureTimingMap.value
      : buildMeasureTimingMap(measures);
    if (measureTimingMap.value.length !== timingMap.length) {
      measureTimingMap.value = timingMap;
    }

    const leadInMs = timingMap[0]?.durationMs ?? 0;
    // Tracks the previous staff entry's time across measures so a grace run at
    // the start of a measure is still bounded by the note right before it. Seeded
    // at -leadInMs so a grace run on the very first staff entry is bounded by the
    // lead-in silence instead of applying unclamped — otherwise
    // entryTimeMs + leadInMs + graceOffsetMs could go negative.
    let previousEntryTimeMs = -leadInMs;

    // Process each measure
    measures.forEach((measure, measureIndex) => {
      const timingSegment = timingMap[measureIndex];
      if (!timingSegment) return;
      const measureDurationMs = timingSegment.durationMs;
      const measureStartMs = timingSegment.startMs;
      const currentSignature = timingSegment.timeSignature;

      const staffEntries = measure.VerticalSourceStaffEntryContainers;

      // Keys (measure + onset) of labels already attached, so each stripped
      // label lands on only the first note at its onset.
      const usedLabelKeys = new Set<string>();

      // TODO: is sorting really needed here?
      // Sort staffEntries by their timestamp within the measure
      staffEntries.sort((a, b) => {
        const timestampA = a.Timestamp ? a.Timestamp.RealValue : 0;
        const timestampB = b.Timestamp ? b.Timestamp.RealValue : 0;
        return timestampA - timestampB;
      });

      // Process entries in this measure
      staffEntries.forEach(staffEntry => {
        if (staffEntry.StaffEntries.length > 0) {
          const measureDurationRealValue = currentSignature.numerator / currentSignature.denominator;

          // entry timestamp real value e.g:
          // 1st 8th note in 6/8 = 0/8
          // 2nd 8th note in 6/8 = 1/8 = 0.125
          // 3rd 8th note in 6/8 = 2/8 = 0.25
          // 4th 8th note in 6/8 = 3/8 = 0.375
          // 5th 8th note in 6/8 = 4/8 = 0.5
          // 6th 8th note in 6/8 = 5/8 = 0.625
          const entryTimestampRealValue = staffEntry.Timestamp ? staffEntry.Timestamp.RealValue : 0;

          const entryTimeMs = measureStartMs + (entryTimestampRealValue / measureDurationRealValue * measureDurationMs);
          const availableGapMs = entryTimeMs - previousEntryTimeMs;
          previousEntryTimeMs = entryTimeMs;

          staffEntry.StaffEntries.forEach(staffEntryItem => {
            const voiceEntries = staffEntryItem.VoiceEntries;
            const graceOffsetMsByVoiceEntry = computeGraceOffsetsMs(voiceEntries, availableGapMs);
            const labelKey = getNoteLabelKey(measureIndex, entryTimestampRealValue);
            // Grace notes share their host note's timestamp, so a leading grace run
            // and the main note it resolves into collide on the same label key —
            // prefer the main note so the label lands on the struck note instead of
            // whichever grace note happens to come first in the voice.
            const labelTargetVoiceEntry = voiceEntries.find(voiceEntry => !voiceEntry.IsGrace) ?? voiceEntries[0];

            voiceEntries.forEach(voiceEntry => {
              const graceOffsetMs = graceOffsetMsByVoiceEntry.get(voiceEntry) ?? 0;
              voiceEntry.Notes
                .filter(note => !note.isRest() && note.Pitch?.FundamentalNote != null)
                .forEach(note => {
                  // Get note name and possible MIDI numbers for this note
                  const noteName = getNoteName(note);
                  const possibleMidiNumbers = getPossibleMidiNumbers(
                    noteName,
                    note.Notehead?.Shape != null ? NoteHeadShape[note.Notehead?.Shape] : undefined
                  );

                  let decoration: 'ghost' | 'rimshot' | undefined;
                  if (note.noteheadColor === GHOST_NOTEHEAD_SENTINEL) {
                    decoration = 'ghost';
                    note.noteheadColor = undefined; // don't let the sentinel actually paint
                  } else if (note.noteheadColor === RIMSHOT_NOTEHEAD_SENTINEL) {
                    decoration = 'rimshot';
                    note.noteheadColor = undefined;
                  }

                  const label = voiceEntry === labelTargetVoiceEntry && !usedLabelKeys.has(labelKey)
                    ? lastPreprocessed?.labelByNoteKey.get(labelKey)
                    : undefined;
                  if (label) usedLabelKeys.add(labelKey);

                  // markRaw: a VexFlow graphical note drags a large object graph
                  // (SVG elements, stems, beams) that must not be proxied.
                  const gNote = osmd.value?.EngravingRules.GNote(note);
                  const graphicalNote = gNote && markRaw(gNote);

                  // A tremolo is one notehead standing in for a run of strikes, so it
                  // becomes several notes to hit, filling the notated duration between
                  // them. They share the parent's graphical note — there is only ever
                  // the one notehead on the page to color.
                  const repeats = getTremoloRepeatCount(note);
                  const repeatDurationMs =
                    (note.Length.RealValue / measureDurationRealValue * measureDurationMs) / repeats;

                  for (let repeat = 0; repeat < repeats; repeat++) {
                    allNotes.push({
                      measureIndex,
                      // Keep one-measure lead-in offset to preserve existing playback alignment.
                      timestamp: entryTimeMs + leadInMs + repeat * repeatDurationMs + graceOffsetMs,
                      x: 0, // will be calculated after the sheet music is rendered
                      played: false,
                      graphicalNote,
                      noteName,
                      possibleMidiNumbers,
                      isTremoloRepeat: repeat > 0,
                      isGrace: voiceEntry.IsGrace,
                      decoration,
                      // Only the primary strike gets the label — a tremolo's repeats
                      // share one notehead, so drawing it again per repeat would stack
                      // duplicate labels on the exact same spot.
                      label: repeat === 0 ? label : undefined,
                    });
                  }
                });
            });
          });
        }
      });
    });

    // TODO: do i really need to sort it here again?
    // Sort notes by timestamp to ensure proper playback order
    allNotes.sort((a, b) => a.timestamp - b.timestamp);

    notes.value = allNotes;
    notes.value.forEach(applyNoteheadDecoration);
    totalMeasures.value = measures.length;

    return allNotes;
  };

  const getMeasureTiming = (measureIndex: number): MeasureTimingSegment | undefined => {
    const segment = measureTimingMap.value[measureIndex];
    if (!segment) return;
    const leadInMs = getTimingLeadInMs();
    return {
      ...segment,
      startMs: segment.startMs + leadInMs,
      endMs: segment.endMs + leadInMs,
    };
  };

  const getMeasureStartMs = (measureIndex: number): number | undefined => {
    const segment = measureTimingMap.value[measureIndex];
    if (!segment) return;
    const nominalStartMs = segment.startMs + getTimingLeadInMs();

    // A measure whose first entry leads with a grace-note run (see
    // computeGraceOffsetsMs) has notes timestamped earlier than its own nominal
    // downbeat. Boundaries anchored on "the start of this measure" (loop
    // points, row layout, measure-click seeking) should land on that run's
    // start rather than cut it off.
    let earliestNoteMs: number | undefined;
    for (const note of notes.value) {
      if (note.measureIndex !== measureIndex) continue;
      if (earliestNoteMs === undefined || note.timestamp < earliestNoteMs) {
        earliestNoteMs = note.timestamp;
      }
    }

    return earliestNoteMs !== undefined ? Math.min(nominalStartMs, earliestNoteMs) : nominalStartMs;
  };

  const getMeasureDurationMs = (measureIndex: number): number | undefined => {
    return measureTimingMap.value[measureIndex]?.durationMs;
  };

  const getRawMeasureAtTime = (ms: number): MeasureTimingSegment | undefined => {
    const segments = measureTimingMap.value;
    if (!segments.length) return;

    if (ms <= segments[0].startMs) return segments[0];
    if (ms >= segments[segments.length - 1].endMs) return segments[segments.length - 1];

    let lo = 0;
    let hi = segments.length - 1;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      const segment = segments[mid];
      if (ms < segment.startMs) {
        hi = mid - 1;
      } else if (ms >= segment.endMs) {
        lo = mid + 1;
      } else {
        return segment;
      }
    }

    return segments[Math.max(0, Math.min(lo, segments.length - 1))];
  };

  const getMeasureAtTime = (ms: number): MeasureTimingSegment | undefined => {
    const leadInMs = getTimingLeadInMs();
    const rawSegment = getRawMeasureAtTime(ms - leadInMs);
    if (!rawSegment) return;
    return {
      ...rawSegment,
      startMs: rawSegment.startMs + leadInMs,
      endMs: rawSegment.endMs + leadInMs,
    };
  };

  const getBeatAtTime = (ms: number): BeatTimingInfo | undefined => {
    const segments = measureTimingMap.value;
    const firstSegment = segments[0];
    if (!firstSegment || firstSegment.beatsPerMeasure <= 0) return;

    const leadInMs = getTimingLeadInMs();

    if (ms < leadInMs) {
      const beatDurationMs = leadInMs / firstSegment.beatsPerMeasure;
      const beatIndexInMeasure = Math.min(
        firstSegment.beatsPerMeasure - 1,
        Math.floor(Math.max(0, ms) / beatDurationMs)
      );
      return {
        measureIndex: 0,
        beatIndexInMeasure,
        beatStartMs: beatIndexInMeasure * beatDurationMs,
        beatDurationMs,
        beatsPerMeasure: firstSegment.beatsPerMeasure,
      };
    }

    const segment = getRawMeasureAtTime(ms - leadInMs);
    if (!segment || segment.beatsPerMeasure <= 0) return;

    const beatDurationMs = segment.durationMs / segment.beatsPerMeasure;
    const timeInMeasureMs = Math.max(0, (ms - leadInMs) - segment.startMs);
    const beatIndexInMeasure = Math.min(
      segment.beatsPerMeasure - 1,
      Math.floor(timeInMeasureMs / beatDurationMs)
    );
    const beatStartMs = leadInMs + segment.startMs + beatIndexInMeasure * beatDurationMs;

    return {
      measureIndex: segment.measureIndex,
      beatIndexInMeasure,
      beatStartMs,
      beatDurationMs,
      beatsPerMeasure: segment.beatsPerMeasure,
    };
  };

  // Highlight a specific note (for gameplay) (debug)
  const highlightNote = (index: number) => {
    if (!osmd.value || index < 0 || index >= notes.value.length) return;

    const currentNote = notes.value[index];
    const currentTimestamp = currentNote.timestamp;

    // TODO: looping over all notes? too inefficient?
    // Find all notes that should be played at this exact timestamp
    const simultaneousNoteIndices = notes.value
      .map((note, idx) => ({ note, idx }))
      .filter(({ note }) => Math.abs(note.timestamp - currentTimestamp) < 10) // Notes within 10ms are considered simultaneous
      .map(({ idx }) => idx);

    // TODO: revisit if this is needed, loop over all the notes each time highlighting a note? too inefficient?
    // Reset all notes to default color first
    notes.value.forEach((note, i) => {
      if (!simultaneousNoteIndices.includes(i) && note.graphicalNote) {
        colorNote(note.graphicalNote as VexFlowGraphicalNote, themeColors.value[HitStatus.Unplayed]);
      }
    });

    // Highlight all simultaneous notes
    simultaneousNoteIndices.forEach(noteIndex => {
      const noteToHighlight = notes.value[noteIndex];
      if (noteToHighlight.graphicalNote) {
        colorNote(noteToHighlight.graphicalNote as VexFlowGraphicalNote, styles.getPropertyValue("--color-surface-overlay")); // Primary color
      }
    });
  };

  // Color a graphical note element
  const colorNote = (graphicalNote: VexFlowGraphicalNote, color: string) => {
    if (!graphicalNote) return;

    try {
      const svgElement = graphicalNote.getSVGGElement();
      const vfnote = svgElement.querySelector("g.vf-note");
      const vfnoteIndex = graphicalNote.vfnoteIndex;

      // Some good info here, but not used yet
      // const vfnote = graphicalNote.vfnote[vfnoteIndex] as Vex.Flow.StemmableNote;
      const path = vfnote?.querySelectorAll(".vf-notehead")[vfnoteIndex]?.children[0] as HTMLElement;
      if (path) {
        if (path) {
          path.setAttribute("fill", color);
        }
      }
    } catch (err) {
      console.error('Failed to color note:', err);
    }
  };

  // Color the notehead shared by a group of notes, from the group's settled status.
  // A tremolo's strokes are several notes over one notehead, so its color belongs to
  // the group rather than to whichever stroke resolved last; ordinary notes pass a
  // single-element group and behave exactly as before.
  const colorNoteheadByStatus = (group: Note[]) => {
    const graphicalNote = group[0]?.graphicalNote;
    if (!graphicalNote) return;
    colorNote(graphicalNote as VexFlowGraphicalNote, themeColors.value[getNoteheadStatus(group)]);
  };

  // Resets to no-sheet-loaded state, and drops the preprocess cache so a
  // same-content re-upload (edge case, but cheap to guard) doesn't reuse a
  // stale result.
  const resetXmlContent = () => {
    xmlContent.value = '';
    lastPreprocessed = null;
  };

  // Handle file upload
  const handleFileUpload = async (file: File): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;
    try {
      // The preprocessed form is derived lazily by the renderMusicXml() that
      // always follows an upload — nothing needed here beyond the raw read.
      xmlContent.value = await readMusicXmlFile(file);
      return true;
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to read file';
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  // Calculate dynamic scroll offset based on tempo and note spacing
  const calculateInitialScrollOffset = (notesToCheck: Note[]) => {
    // Grace notes crowd close to their main note (see excludeGraceNotes) and
    // would skew the initial pixels-per-ms estimate if either sampled note
    // landed on one, so derive it from the grace-free subset instead.
    const trackedNotes = excludeGraceNotes(notesToCheck ?? []);
    if (trackedNotes.length < 2) {
      // default when there is just one note
      return 300;
    }
    const firstNoteTimestamp = trackedNotes[0].timestamp;

    let secondNoteTimestamp = firstNoteTimestamp;
    let secondNoteIndex = 0;

    for (let i = 1; i < trackedNotes.length; i++) {
      if (trackedNotes[i].timestamp > firstNoteTimestamp) {
        secondNoteTimestamp = trackedNotes[i].timestamp;
        secondNoteIndex = i;
        break;
      }
    }

    const firstNoteX = trackedNotes[0].x;
    const secondNoteX = trackedNotes[secondNoteIndex].x;

    const horizontalDistance = (secondNoteX - firstNoteX);
    const timeDifferenceMs = secondNoteTimestamp - firstNoteTimestamp;
    const pixelsPerMs = horizontalDistance / timeDifferenceMs;
    return firstNoteX - pixelsPerMs * firstNoteTimestamp;
  };

  watch(settings.currentTheme, () => {
    themeColors.value = readThemeColors(settings.currentTheme.value);
    if (!osmd.value) return;
    recolorOsmdByTheme(container.value, settings.currentTheme.value);
    notes.value.forEach(applyNoteheadDecoration);
  });

  watch(isLoaded, () => {
    if (isLoaded.value) {
      recolorOsmdByTheme(container.value, settings.currentTheme.value);
    }
  });

  watch(currentMeasureIndex, () => {
    updateMeasureVisibility();
  });

  const setRenderMode = async (mode: 'single' | 'multi', zoom?: number) => {
    if (!osmd.value) return false;
    const xml = xmlContent.value;
    if (!xml) return false;

    isLoading.value = true;
    error.value = null;
    try {
      await renderMusicXml(xml, zoom, mode);
      await extractSongInfo();
      await processNotes();
      isLoaded.value = true;
      return true;
    } catch (err: unknown) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Failed to switch sheet music render mode';
      error.value = message;
      createToast({
        type: 'error',
        message,
      });
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    osmd,
    container,
    isLoaded,
    isLoading,
    error,
    currentMeasureIndex,
    totalMeasures,
    notes,
    // UI default only; playback timing comes from measureTimingMap/getBeatAtTime.
    initialBpm,
    measureTimingMap,
    songTitle,
    timeSignature,
    songDuration,
    staffHeight,
    measureHeight,
    renderMode,
    xmlContent,
    resetXmlContent,
    colorNote,
    colorNoteheadByStatus,
    initOSMD,
    renderOsmd,
    renderMusicXml,
    loadMusicXML,
    setRenderMode,
    processNotes,
    highlightNote,
    handleFileUpload,
    getNoteName,
    getPossibleMidiNumbers,
    getMeasureTiming,
    getMeasureStartMs,
    getMeasureDurationMs,
    getMeasureAtTime,
    getBeatAtTime,
    calculateInitialScrollOffset,
    updateMeasureVisibility,
    showAllMeasures
  };
}
